import axios from 'axios'
import type { Feature, FeatureCollection, Point } from 'geojson'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  SubjectFeature,
  SubjectFeatureCollection,
  SubjectSearchApiResponse,
} from '../../common/subjectTypes'
import { useAppSelector } from '../../redux/hooks'
import { parseCuzkAddress } from './parseCuzkAddress'

/**
 * Hook returns a GeoJSON FeatureCollection of subjects with geometry (or null)
 * and their properties from the subject API.
 *
 * @param name - Subject name
 * @param address - Subject address (optional)
 * @param history - Only for Tester role (optional)
 *
 */
export const useFetchSubjectSearch = (
  name?: string,
  address?: string
): {
  fetching: boolean
  error: boolean
  data: SubjectFeatureCollection
  refetch: (name?: string, address?: string, history?: boolean) => void
} => {
  const token = useAppSelector(state => state.myProfile.token)

  const [fetching, setFetching] = useState(false)
  const [error, setError] = useState(false)
  const [data, setData] = useState<SubjectFeatureCollection>({
    type: 'FeatureCollection',
    features: [],
  })

  const prevNameRef = useRef<string | undefined>()
  const prevAddressRef = useRef<string | undefined>()
  const isFetchingRef = useRef(false)

  const fetchData = useCallback(
    async (
      customName?: string,
      customAddress?: string,
      customHistory?: boolean
    ) => {
      const searchName = customName ?? name
      const searchAddress = customAddress ?? address
      const searchHistory = customHistory ?? false

      if (isFetchingRef.current) return

      isFetchingRef.current = true
      setFetching(true)
      setError(false)

      try {
        const params = new URLSearchParams()
        if (searchAddress) params.append('adresa', searchAddress)
        if (searchName) params.append('nazev', searchName)
        params.append('vcetneHistorie', searchHistory ? 'true' : 'false')
        params.append('access_token', token || '')

        const urlSubjects = `https://data.regesta.cz/DrmApi/api/v1.0/subjekty?${params}`
        const response = await axios.get<SubjectSearchApiResponse[]>(
          urlSubjects
        )
        const subjects = response.data

        if (!subjects.length) {
          setData({ type: 'FeatureCollection', features: [] })
          return
        }

        const addressCodes = Array.from(
          new Set(
            subjects
              .filter(s => s.adresniMistoKod != null)
              .map(s => s.adresniMistoKod as number)
          )
        )

        const where = `KOD IN (${addressCodes.join(',')})`
        const arcgisUrl =
          'https://ags.cuzk.gov.cz/arcgis/rest/services/RUIAN/MapServer/1/query'
        const form = new URLSearchParams()
        form.append('where', where)
        form.append('outFields', 'kod,adresa')
        form.append('f', 'geojson')

        const arcgisResponse = await axios.post<FeatureCollection<Point>>(
          arcgisUrl,
          form,
          { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
        )
        const features = arcgisResponse.data.features

        const featuresMap = new Map<number, Feature<Point>>(
          features.map(f => [f.properties?.kod as number, f])
        )

        const subjectFeatures: SubjectFeature[] = subjects.map(subject => {
          const feature = subject.adresniMistoKod
            ? featuresMap.get(subject.adresniMistoKod)
            : undefined

          const { ulice, castObce, psc, obec } = parseCuzkAddress(
            feature?.properties?.adresa
          )
          return {
            type: 'Feature',
            geometry: feature?.geometry ?? null,
            properties: {
              id: subject.id,
              nazev: subject.nazev,
              adresa: feature?.properties?.adresa || subject.adresa || '',
              adresniMistoKod: subject.adresniMistoKod ?? null,
              historicky: subject.historicky,
              ulice,
              castObce,
              psc,
              obec,
            },
          }
        })

        setData({ type: 'FeatureCollection', features: subjectFeatures })
      } catch (err) {
        setError(true)
        setData({ type: 'FeatureCollection', features: [] })
      } finally {
        setFetching(false)
        isFetchingRef.current = false
        prevNameRef.current = name
        prevAddressRef.current = address
      }
    },
    [address, name, token]
  )

  useEffect(() => {
    if (isFetchingRef.current) return

    const hasChanged =
      prevNameRef.current !== name || prevAddressRef.current !== address
    const hasValidInput = Boolean(name?.trim() || address?.trim())

    if (hasValidInput && hasChanged) {
      fetchData()
    } else if (!hasValidInput) {
      setData({ type: 'FeatureCollection', features: [] })
      setError(false)
    }
  }, [fetchData, name, address])

  const refetch = useCallback(
    (newName?: string, newAddress?: string, newHistory?: boolean) => {
      fetchData(newName, newAddress, newHistory)
    },
    [fetchData]
  )

  return { fetching, error, data, refetch }
}
